import { createContext, useState, useContext, useEffect } from 'react';
import { inventoryApi, sheetsApi } from '../services/api.service';
import { useJob } from './JobContext';

const InventoryContext = createContext();

export const useInventory = () => useContext(InventoryContext);

export function InventoryProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [availableSpreadsheets, setAvailableSpreadsheets] = useState([]);
  const [inventorySummary, setInventorySummary] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  
  // Constants for localStorage management
  const MANUAL_ITEMS_KEY = 'manualInventoryItems';
  const MAX_MANUAL_ITEMS = 100; // Maximum number of manual items to store
  const MAX_ITEM_AGE_DAYS = 30; // Maximum age of items in days before cleanup
  
  // Function to clean up manual inventory items
  const cleanupManualItems = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    
    // 1. Remove any items older than MAX_ITEM_AGE_DAYS
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - MAX_ITEM_AGE_DAYS);
    
    let cleanedItems = items.filter(item => {
      // Skip items with invalid date format
      if (!item.lastUpdated) return true;
      
      const itemDate = new Date(item.lastUpdated);
      return !isNaN(itemDate.getTime()) && itemDate >= cutoffDate;
    });
    
    // 2. If still too many items, keep only the most recent MAX_MANUAL_ITEMS
    if (cleanedItems.length > MAX_MANUAL_ITEMS) {
      // Sort by date (newest first)
      cleanedItems.sort((a, b) => {
        const dateA = new Date(a.lastUpdated || 0);
        const dateB = new Date(b.lastUpdated || 0);
        return dateB - dateA;
      });
      
      // Keep only the MAX_MANUAL_ITEMS most recent items
      cleanedItems = cleanedItems.slice(0, MAX_MANUAL_ITEMS);
      
      console.log(`Cleaned up manual inventory items. Keeping ${cleanedItems.length} most recent items.`);
    }
    
    // Update localStorage with cleaned items
    try {
      localStorage.setItem(MANUAL_ITEMS_KEY, JSON.stringify(cleanedItems));
    } catch (error) {
      console.error('Error saving cleaned items to localStorage:', error);
    }
    
    return cleanedItems;
  };
  
  // Load manual inventory items from localStorage initially with cleanup
  const [manualInventoryItems, setManualInventoryItems] = useState(() => {
    try {
      const savedItems = localStorage.getItem(MANUAL_ITEMS_KEY);
      let items = savedItems ? JSON.parse(savedItems) : [];
      
      // Apply cleanup on initial load
      items = cleanupManualItems(items);
      
      return items;
    } catch (error) {
      console.error('Error loading manual inventory items from localStorage:', error);
      return [];
    }
  });
  

  // Get current job from JobContext - with safety check
  const jobContext = useJob();
  const currentJob = jobContext?.currentJob;

  // Configuration for sheets (from current job or environment variables)
  const config = {
    spreadsheetId: currentJob?.spreadsheetId || import.meta.env.VITE_SPREADSHEET_ID || '',
    sheetName: currentJob?.sheetName || import.meta.env.VITE_SHEET_NAME || 'Inventory',
  };
  
  // DISABLED: Auto-loading inventory summary to prevent repeated API calls
  // The inventory summary will be loaded on-demand instead
  /*
  useEffect(() => {
    // Skip if no spreadsheet ID is available
    if (!config.spreadsheetId) return;
    
    // Only load initially, not on re-renders
    const shouldLoad = currentJob && (
      !inventorySummary || Object.keys(inventorySummary).length === 0
    );
    
    if (shouldLoad) {
      // Load inventory summary only when necessary
      loadInventorySummary().catch(err => {
        console.warn('Could not load inventory summary, using local data:', err);
        // Don't set error state to avoid showing error messages when we have manual items
      });
    }
  }, [currentJob]); // eslint-disable-line react-hooks/exhaustive-deps
  */
  
  // Load inventory summary
  const loadInventorySummary = async (options = {}) => {
    // Remove the spreadsheetId check - we're using database now
    
    setLoading(true);
    setError(null);
    
    try {
      // Use the database-backed inventory summary endpoint (no spreadsheet params)
      const response = await inventoryApi.getInventorySummary(options);
      
      // API now returns standardized format: { success: true, data: [...], meta: {...} }
      let data;
      if (response.data && response.data.success && response.data.data) {
        // Extract the actual data array from standardized format
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Fallback for direct array response
        data = response.data;
      } else {
        // Unexpected format
        console.error('Unexpected inventory summary format:', response.data);
        data = [];
      }
      
      setInventorySummary(data);
      console.log('Successfully loaded inventory summary');
      return data;
    } catch (err) {
      // Determine if it's a connection error or some other issue
      const errorMessage = typeof err === 'string' ? err : err.message || 'Failed to load inventory summary';
      const isConnectionError = 
        errorMessage.includes('ECONNREFUSED') || 
        errorMessage.includes('network error') ||
        errorMessage.includes('Failed to fetch');
      
      if (isConnectionError) {
        console.warn('Could not connect to server, using locally cached data:', errorMessage);
        // Don't show connection errors to user if we have manual items
        if (manualInventoryItems.length === 0) {
          setError('Network connection error. Any items you add will be saved locally.');
        }
      } else {
        console.error('Error loading inventory summary:', err);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * List available Google Sheets spreadsheets
   */
  const listSpreadsheets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await sheetsApi.listSpreadsheets();
      setAvailableSpreadsheets(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Error listing spreadsheets:', err);
      setError(typeof err === 'string' ? err : 'Failed to list spreadsheets');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get inventory data (legacy method name for compatibility)
   * Now uses the database-backed inventory endpoint
   */
  const getSheetData = async (spreadsheetId, range) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the inventory summary endpoint instead of sheets
      const response = await inventoryApi.getInventorySummary();
      return response.data;
    } catch (err) {
      console.error('Error getting inventory data:', err);
      setError(typeof err === 'string' ? err : 'Failed to get inventory data');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Search inventory by query
   */
  const searchInventory = async (query) => {
    setLoading(true);
    setError(null);
    
    try {
      // Only pass the query parameter, no spreadsheet params
      const response = await inventoryApi.searchInventory(query);
      
      setSearchResults(response.data.data);
      return response.data.data;
    } catch (err) {
      console.error('Error searching inventory:', err);
      setError(typeof err === 'string' ? err : 'Failed to search inventory');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Receive inventory items - ensures items are sent to database AND kept in localStorage
   * for immediate display while waiting for API response
   */
  const receiveItems = async (items) => {
    setLoading(true);
    setError(null);
    
    // For immediate display, prepare the local inventory items first
    const newItems = items.map(item => ({
      id: item.id || `${item.barcode}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      barcode: item.barcode,
      description: item.description,
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serialNumber: item.serialNumber || item.serial_number || '',
      totalQuantity: parseInt(item.quantity, 10),
      quantity: parseInt(item.quantity, 10), // Also store as quantity for compatibility
      warehouse: item.warehouse || 'Main',
      rack: item.rack || item.zone || '',
      bay: item.bay || '',
      lastUpdated: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      isManualItem: true // Mark as manual item for display purposes
    }));
    
    // Update state with new items while preserving existing ones (for immediate feedback)
    const updatedItems = [...manualInventoryItems, ...newItems];
    
    // Update state immediately - don't wait for API
    setManualInventoryItems(updatedItems);
    
    // Save to localStorage as backup/cache
    localStorage.setItem(MANUAL_ITEMS_KEY, JSON.stringify(updatedItems));
    console.log(`Updated local cache with ${newItems.length} new items`);
    
    // Now try to persist to the API (database)
    try {
      // Try the newer equipment-items endpoint which expects barcode directly
      const equipmentItems = items.map(item => ({
        barcode: item.barcode,
        description: item.description || 'Equipment Item',
        manufacturer: item.manufacturer || '',
        model: item.model || '',
        serialNumber: item.serialNumber || item.serial_number || '',
        quantity: parseInt(item.quantity || item.totalQuantity || 1, 10),
        condition: 'new',
        status: 'available',
        warehouseId: item.warehouse || item.warehouseId || 'ad5c5f06-4e66-433e-a637-437773457233',
        rackId: item.rack || item.zone || item.rackId || item.zoneId || '1476c4d0-7795-489c-9efb-4ff15e3f2418',
        bayId: item.bay || item.bayId || '',
        categoryId: item.category_id || item.categoryId || null,
        notes: item.notes || ''
      }));
      
      console.log('Attempting to create equipment items via /equipment-items endpoint:', equipmentItems);
      
      try {
        // Try the newer endpoint first
        const response = await inventoryApi.createEquipmentItems(equipmentItems);
        console.log('Successfully created equipment items via new endpoint');
        
        // Reload inventory summary after receiving items
        await loadInventorySummary();
        
        // Force components to refresh by updating timestamp
        setLastUpdateTime(Date.now());
        
        setLoading(false);
        return response.data;
      } catch (equipmentError) {
        console.log('Equipment-items endpoint failed, trying legacy receive endpoint:', equipmentError.message);
        
        // Fallback to the receive endpoint
        const apiItems = items.map(item => ({
          barcode: item.barcode,
          description: item.description,
          quantity: parseInt(item.quantity || item.totalQuantity || 1, 10), // Ensure quantity is a number
          manufacturer: item.manufacturer,
          model: item.model,
          serialNumber: item.serialNumber || item.serial_number,
          warehouseId: item.warehouse || item.warehouseId || 'ad5c5f06-4e66-433e-a637-437773457233',
          rackId: item.rack || item.zone || item.rackId || item.zoneId || '1476c4d0-7795-489c-9efb-4ff15e3f2418',
        bayId: item.bay || item.bayId || '',
          notes: item.notes,
          categoryId: item.category_id || item.categoryId,
          status: item.status || 'Received'
        }));
        
        // Send to the API to ensure items are saved in the database
        console.log('Sending items to legacy API for database storage:', apiItems);
        try {
          const response = await inventoryApi.receiveItems(apiItems);
          console.log('📄 Legacy API response:', response.data);
          
          // Check if it's actually a success or partial success
          if (response.data?.status === 'success') {
            console.log('✅ Successfully saved to database via legacy endpoint');
          } else if (response.data?.status === 'partial') {
            console.log('⚠️ Partial success - items may be localStorage only:', response.data.message);
          } else {
            console.log('❓ Unexpected response status:', response.data?.status);
          }
          
          // Reload inventory summary after receiving items
          await loadInventorySummary();
          
          // Force components to refresh by updating timestamp
          setLastUpdateTime(Date.now());
          
          setLoading(false);
          return response.data;
        } catch (legacyError) {
          console.error('❌ Legacy endpoint also failed:', legacyError);
          console.error('Legacy error response:', legacyError.response?.data);
          console.error('Detailed errors:', legacyError.response?.data?.detailedErrors);
          throw legacyError; // Re-throw to trigger the outer catch block
        }
      }
    } catch (err) {
      console.error('Error receiving items in API:', err);
      
      // Don't show error to user if the local save worked - items are safe in localStorage
      // and will be synced later when the API is available
      const errorMsg = `API storage failed, but items are saved locally: ${typeof err === 'string' ? err : 'Connection issue'}`;
      console.warn(errorMsg);
      
      // Don't set visible error since items are safe in localStorage
      // setError(errorMsg);
      
      setLoading(false);
      
      // Return a mock successful response so the user experience isn't interrupted
      return {
        status: 'partial_success',
        message: 'Items received and saved locally, will sync to database when connection is available',
        timestamp: new Date().toISOString()
      };
    }
  };

  /**
   * Checkout inventory items for a job
   * @param {Array} items - Array of items to checkout
   * @param {string} jobNumber - Job number to checkout items for
   * @returns {Promise} - Promise with checkout response
   */
  const checkoutItems = async (items, jobNumber) => {
    setLoading(true);
    setError(null);
    
    try {
      // Update inventory in database
      const response = await inventoryApi.checkoutItems(
        items,
        jobNumber
      );
      
      // If JobContext is available, try to update job equipment data as well
      if (jobContext && jobContext.updateJobEquipment) {
        // Process each item to add to job equipment list
        for (const item of items) {
          if (item.siteId) {
            try {
              // Format the equipment data for the job
              const equipmentData = {
                id: item.itemId || item.id, // Support both itemId and id
                description: item.description,
                quantity: item.quantity || 1,
                isReceived: false, // Items are marked as pending when first checked out
                notes: item.notes || '',
                timestamp: new Date().toISOString()
              };
              
              // Update job equipment data in JobContext
              await jobContext.updateJobEquipment(jobNumber, item.siteId, equipmentData);
            } catch (jobErr) {
              console.error('Error updating job equipment:', jobErr);
              // Don't throw here to avoid blocking inventory checkout completion
            }
          }
        }
      }
      
      // Refresh inventory summary after checkout
      await loadInventorySummary();
      
      return response.data;
    } catch (err) {
      console.error('Error checking out items:', err);
      setError(typeof err === 'string' ? err : 'Failed to checkout items');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * V1 Legacy method - Deprecated in favor of equipmentService.returnEquipment()
   * Use equipmentService.returnEquipment() for V2 individual equipment returns
   * @deprecated Use equipmentService.returnEquipment() instead
   */
  const returnItems = async (items, jobNumber) => {
    console.warn('returnItems() is deprecated - use equipmentService.returnEquipment() for V2 architecture');
    setLoading(true);
    setError(null);
    
    try {
      const response = await inventoryApi.returnItems(
        items,
        jobNumber
      );
      
      return response.data;
    } catch (err) {
      console.error('Error returning items:', err);
      setError(typeof err === 'string' ? err : 'Failed to return items');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    loading,
    error,
    searchResults,
    setSearchResults, // Expose this so we can clear search results after checkout
    availableSpreadsheets,
    inventorySummary,
    manualInventoryItems, // Expose the manually added items
    lastUpdateTime, // Expose update timestamp
    config,
    listSpreadsheets,
    getSheetData,
    searchInventory,
    receiveItems,
    checkoutItems,
    returnItems,
    loadInventorySummary,
    clearError,
  };

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
}
